import { useCallback, useState } from 'react';
import * as Sentry from '@sentry/nextjs';
import logger from '@/utils/logger';
import { createWrappedError } from '@/utils/errors';
import useOnSubmit from './useOnSubmit';
import { useToast } from '@/hooks/useToast';
import { getRecaptchaToken } from './utils';
import { CODES as ErrCodes } from './errors';
const PREFIX = 'features: Auth: hooks: useSignupForm:';

const useSignupForm = _ref => {
  let {
    onGoogleAuthSuccess,
    onSignupSuccess
  } = _ref;
  const {
    0: isNewsletterChecked,
    1: setNewsletterChecked
  } = useState(false);
  const {
    addToast: sendNotification
  } = useToast();
  const {
    errors,
    isLoading,
    isGoogleSignupLoading,
    onSubmit,
    onSubmitWithGoogle
  } = useOnSubmit(); // Notification user account created

  const successNotification = useCallback(msg => {
    sendNotification({
      description: msg,
      type: 'success'
    });
  }, [sendNotification]); // Prompt user to login if their account already exists

  const handleAccountAlreadyExists = useCallback(() => {
    sendNotification({
      description: 'Looks like you already have an account created. Please login',
      type: 'info'
    });
  }, [sendNotification]); // Submit Email/Password Signup

  const handleEmailPassSignUp = useCallback(async data => {
    let token = '';

    try {
      token = await getRecaptchaToken();
    } catch (err) {
      const wrappedError = createWrappedError(`${PREFIX} handleEmailPassSignUp: recaptcha token request request failed`, err);
      logger.error(wrappedError);
      Sentry.captureException(wrappedError);
      sendNotification({
        type: 'error',
        description: err.userMessage || 'Unexpected issue, please try again.'
      });
      return;
    }

    try {
      await onSubmit(token, data);
    } catch (err) {
      const wrappedError = createWrappedError(`${PREFIX} handleEmailPassSignUp: request failed with code "${err.code}"`, err);
      logger.error(wrappedError);
      Sentry.captureException(wrappedError);
      sendNotification({
        type: 'error',
        description: err.userMessage || 'Unexpected issue, please try again.'
      }); // Accept email verification errors as successful

      if (err.code !== ErrCodes.emailVerification) {
        return; // Avoid redirecting on failure
      } else if (err.code === ErrCodes.accountExists) {
        handleAccountAlreadyExists();
        return;
      }
    }

    successNotification('Account created successfully! Please check your email to verify your account.');
    typeof onSignupSuccess === 'function' && onSignupSuccess();
  }, [onSubmit, onSignupSuccess, sendNotification, successNotification, handleAccountAlreadyExists]); // Submit Google OAuth Signup

  const handleGoogleSignUp = useCallback(async credential => {
    let reCaptchaToken = '';

    try {
      reCaptchaToken = await getRecaptchaToken();
    } catch (err) {
      const wrappedError = createWrappedError(`${PREFIX} handleGoogleSignUp: recaptcha token request request failed`, err);
      logger.error(wrappedError);
      Sentry.captureException(wrappedError);
      sendNotification({
        type: 'error',
        description: err.userMessage || 'Unexpected issue, please try again.'
      });
      return;
    }

    try {
      await onSubmitWithGoogle(reCaptchaToken, credential, isNewsletterChecked);
    } catch (err) {
      // Do not show error if user closed the popup
      if (err.code === ErrCodes.popupClosedByUser) {
        return;
      } // Account exists error


      if (err.code === ErrCodes.accountExists) {
        handleAccountAlreadyExists();
        return;
      } // Unexpected errors


      const wrappedErr = createWrappedError(`${PREFIX} handleGoogleSignUp: google auth submission failed`, err);
      logger.error(wrappedErr);
      Sentry.captureException(wrappedErr);
      sendNotification({
        type: 'error',
        description: err.userMessage || 'Unexpected issue, please try again.'
      });
      return;
    }

    successNotification('Account created successfully! Please login with your Google account to continue.');
    typeof onGoogleAuthSuccess === 'function' && onGoogleAuthSuccess();
    typeof onSignupSuccess === 'function' && onSignupSuccess();
  }, [isNewsletterChecked, onSubmitWithGoogle, onGoogleAuthSuccess, onSignupSuccess, successNotification, sendNotification, handleAccountAlreadyExists]);
  return {
    errors,
    isLoading,
    isNewsletterChecked,
    setNewsletterChecked,
    isGoogleSignupLoading,
    handleEmailPassSignUp,
    handleGoogleSignUp
  };
};

export default useSignupForm;